/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { rokidBaseUrl } from '@coze-arch/bot-http';

import { axiosInstance, type BotAPIRequestConfig } from './axios';

// rokid: 对话日志相关请求/响应定义，用于替代旧的调试面板查询接口
/**
 * 对话日志列表请求参数
 */
export interface ConversationLogListRequest {
  /** agentId */
  bot_id: string;
  /** 模式（线上-online，调试-draft），不传默认调试 */
  mode?: 'online' | 'draft';
  /** 开始时间，毫秒时间戳 */
  start_time: number;
  /** 结束时间，毫秒时间戳 */
  end_time: number;
  /** 执行状态（成功-success，失败-fail），不传查全部 */
  status?: 'success' | 'fail';
  /** 记录起始游标，起始为0 */
  cursor?: string;
  /** 获取记录数量 */
  count?: number;
}

/**
 * 单条对话日志
 */
export interface ConversationLogItem {
  /** 本次调用的日志id */
  log_id: string;
  /** 用户问题 */
  input: string;
  /** 执行状态（成功-success，失败-fail） */
  status: 'success' | 'fail';
  /** 整体耗时，ms */
  latency: number;
  /** 首次响应耗时，ms */
  first_latency: number;
  /** 输入token数 */
  input_tokens: number;
  /** 输出token数 */
  output_tokens: number;
  /** 开始时间，毫秒时间戳 */
  start_time: number;
  /** 结束时间，毫秒时间戳 */
  end_time: number;
}

/**
 * 对话日志列表响应
 */
export interface ConversationLogListResponse {
  code: number;
  msg: string;
  timestamp: number;
  uuid: string;
  data: {
    log_list: ConversationLogItem[];
    /** 下个游标，null则没有下一条 */
    next_cursor: string | null;
    /** 是否有更多数据 */
    has_more: boolean;
  };
}

/**
 * 对话日志详情请求参数
 */
export interface ConversationLogDetailRequest {
  /** 本次调用的traceId（即 log_id） */
  log_id: string;
}

/**
 * 调用树节点
 */
export interface ConversationLogNodeItem {
  /** 当前节点id */
  id: string;
  /** 父节点id */
  parent_id: string;
  /** 类型（LLM、agent、workflow 等） */
  type: string;
  /** 调用类型（agent、workflow） */
  call_type: string;
  /** 状态（成功-success，失败-fail） */
  status: 'success' | 'fail';
  /** 名称 */
  name: string;
  /** 整体耗时，ms */
  latency: number;
  /** 开始时间，毫秒时间戳 */
  start_time: number;
  /** 结束时间，毫秒时间戳 */
  end_time: number;
  /** 输入token数 */
  input_tokens: number;
  /** 输出token数 */
  output_tokens: number;
  /** 首次响应耗时，ms（仅模型节点及其父节点） */
  first_latency?: number;
  /** 首字符回复时间，毫秒时间戳（仅模型节点及其父节点） */
  first_start_time?: number;
  /** 模型（仅模型节点） */
  model?: string;
  /** 回复随机性（仅模型节点） */
  temperature?: number;
  /** 最大回复长度（仅模型节点） */
  max_tokens?: number;
  /** 输入 */
  input?: string;
  /** 输出 */
  output?: string;
  /** 图标 URL */
  icon_url?: string;
  /** 工作流唯一标识（存在时表示工作流节点） */
  workflow_id?: string;
  // rokid: 阻塞节点的 log_id，存在时表示该节点被此 log 阻塞，详情页点击可跳转对应 log
  /** 阻塞节点的 log_id，存在时表示该节点被此 log 阻塞 */
  interrupt_log_id?: string;
  query_type?: string;
  /** Query输入方式: 手动输入 ... */
  query_input_method?: string;
  /** 是否是重新生成 */
  is_regenerate?: boolean;
  /** 首字母回复时间戳，单位毫秒 */
  start_time_first_resp?: number;
  /** 首字母回复时延，单位毫秒 */
  latency_first_resp?: number;
  /** 客户端类型 */
  client_type?: string;
  /** 用户的操作系统，如 iOS/Android */
  os?: string;
  /** 用户操作系统版本 */
  os_version?: string;
  /** 浏览器类型 */
  browser_type?: string;
  /** 浏览器版本 */
  browser_version?: string;
  /** 调试信息中的 push uuid */
  push_uuid?: string;
  /** 建议消息 */
  suggestions?: string;
  /** 对话场景 */
  dialog_scene?: string;
  /** 消息唯一标识 */
  message_id?: string;
  /** 简洁输入 */
  simple_input?: string;
  /** workflow / plugin / knowledge 等节点的工作流节点 id */
  workflow_node_id?: string;
  /** 工作流执行 ID */
  execute_id?: string;
  /** 工作流版本 */
  workflow_version?: string;
  /** 工作流 schema 类型 */
  workflow_schema_type?: number;
  /** 工作流子执行 ID */
  sub_execute_id?: string;
  /** Prompt */
  prompt?: string;
  /** 最大回复字符长度 */
  max_length_resp?: number;
  /** 批处理任务序号 */
  task_index?: number;
  /** 批处理任务总数 */
  task_total?: number;
  /** 插件唯一标识 */
  plugin_id?: string;
  /** 工具唯一标识 */
  tool_id?: string;
  /** 工具名称 */
  tool_name?: string;
  /** Knowledge 唯一标识 */
  knowledge_id?: string;
  /** 变量调用方法 */
  action?: string;
  /** 输出模式 */
  output_mode?: string;
  /** 应答内容 */
  answer_content?: string;
  /** Agent 模式类型 */
  agent_type?: string;
  /** 上下文模式 */
  context_mode?: string;
  /** 记忆会话轮数 */
  dialog_round?: number;
  /** Agent ID */
  agent_id?: string;
  /** hook 服务类型 */
  hook_type?: string;
  /** hook URI */
  hook_uri?: string;
  /** hook 响应码 */
  hook_resp_code?: string;
  /** 是否流式 */
  is_stream?: boolean;
}

/**
 * 对话日志详情响应
 */
export interface ConversationLogDetailResponse {
  code: number;
  msg: string;
  timestamp: number;
  uuid: string;
  data: {
    node_list: ConversationLogNodeItem[];
  };
}

// rokid: 对话日志 API 服务实现，对接后端新路由
/**
 * 对话日志 API
 */
class ConversationLogApiService {
  /**
   * 查询对话日志列表
   * POST /conversation/log/list
   */
  listConversationLogs(
    req: ConversationLogListRequest,
    options: BotAPIRequestConfig = {},
  ): Promise<ConversationLogListResponse> {
    const url = `${rokidBaseUrl}/v1/conversation/log/list`;
    const headers = {
      'Agw-Js-Conv': 'str',
    };
    return axiosInstance.request({
      url,
      method: 'POST',
      data: req,
      headers,
      ...options,
    });
  }

  /**
   * 查询对话日志详情（调用树）
   * POST /conversation/log/detail
   */
  getConversationLogDetail(
    req: ConversationLogDetailRequest,
    options: BotAPIRequestConfig = {},
  ): Promise<ConversationLogDetailResponse> {
    const url = `${rokidBaseUrl}/v1/conversation/log/detail`;
    const headers = {
      'Agw-Js-Conv': 'str',
    };
    return axiosInstance.request({
      url,
      method: 'POST',
      data: req,
      headers,
      ...options,
    });
  }
}

export const conversationLogApi = new ConversationLogApiService();
